/**
 * Wetter-Typen und alles, was OHNE zod auskommt.
 *
 * Bewusst getrennt von `openMeteo.ts`: dort steht das zod-Schema fuer die
 * Antwort des Wetterdienstes, und zod kostet rund 26 kB gzip. Die Startseite
 * braucht nur `describeWeather` - ein reines Nachschlagewerk. Lagen beide in
 * einer Datei, zoege jeder Erststart zod mit, obwohl das Wetter standardmaessig
 * ausgeschaltet ist.
 */
import type { LocalDate } from '@/domain/types';

export interface DailyWeather {
  date: LocalDate;
  code: number;
  maxC: number;
  minC: number;
  rainChance: number | null;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
  name: string;
}

/** Berlin als Ausgangspunkt, bis ein eigener Ort gesetzt wird. */
export const DEFAULT_LOCATION: Coordinates = {
  latitude: 52.52,
  longitude: 13.405,
  name: 'Berlin',
};

/**
 * Welches Wettersymbol (F-39): ein Name statt eines Emojis. Emojis fehlen je
 * nach System in der Schrift - auf dem Chromebook erschien Nebel als leeres
 * Kaestchen. Gezeichnet werden die Symbole in `ui/composites/WetterSymbol.tsx`
 * mit derselben Symbolbibliothek wie der Rest der App.
 */
export type WetterSymbolName =
  | 'klar' | 'sonnig' | 'bedeckt' | 'nebel' | 'niesel' | 'regen' | 'schnee' | 'schauer' | 'schneeschauer' | 'gewitter';

/**
 * WMO-Wettercodes als Symbol und deutscher Text.
 * Zusammengefasst auf das, was man auf einen Blick unterscheiden will.
 */
export function describeWeather(code: number): { symbol: WetterSymbolName; label: string } {
  if (code === 0) return { symbol: 'klar', label: 'Klar' };
  if (code <= 2) return { symbol: 'sonnig', label: '\u00dcberwiegend sonnig' };
  if (code === 3) return { symbol: 'bedeckt', label: 'Bedeckt' };
  if (code <= 48) return { symbol: 'nebel', label: 'Nebel' };
  if (code <= 57) return { symbol: 'niesel', label: 'Nieselregen' };
  if (code <= 67) return { symbol: 'regen', label: 'Regen' };
  if (code <= 77) return { symbol: 'schnee', label: 'Schnee' };
  if (code <= 82) return { symbol: 'schauer', label: 'Schauer' };
  if (code <= 86) return { symbol: 'schneeschauer', label: 'Schneeschauer' };
  return { symbol: 'gewitter', label: 'Gewitter' };
}
